/**
 * Live module step 4 — Kommunikation (PRD §11, PLAN.md D-49, V-17).
 *
 * Layout: placeholder help, invitation section (template + recipients), then the reminder half
 * (settings + recipients with Missing/Entered badges, selection send, status strip, reminder log).
 *
 * Reply-To is app-wide (Admin Settings SMTP / SMTP_REPLY_TO) — not per-project.
 * Schedule is weekday + hour; "Missing" = incomplete week-completion in the current Berlin month.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertCircle, Check, Copy, Mail, Save, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { describeError } from "@/lib/errors";
import { toastApiError } from "@/lib/rate-limit";
import {
  useAdminCopyInviteLink,
  useAdminSaveReminders,
  useAdminSendInvites,
  useAdminSendReminders,
  useProjectReminders,
  type ReminderAssignee,
} from "@/lib/admin";
import { formatPersonName } from "@/lib/format";
import { berlinHour, berlinToday, isoWeekday } from "@/shared/dates";

const DEFAULT_INVITE_SUBJECT_DE = "Zeiterfassung für {project}";
const DEFAULT_INVITE_BODY_DE = `Hallo {first_name},

für die Forschungszulage erfassen wir unsere Arbeitszeiten im Projekt „{project}". Bitte melde dich unter {link} mit dieser E-Mail-Adresse an — du erhältst automatisch einen Login-Link (Magic Link), um deine Stunden und den F&E-Anteil einzutragen. Kein Passwort nötig.

Bitte trage deine Stunden einmal pro Woche ein (spätestens zum Monatsende). Das dauert weniger als 2 Minuten pro Woche und ist die Grundlage für die Förderung.

Danke!`;

const DEFAULT_REMINDER_SUBJECT_DE = "Erinnerung: Stunden für {month} noch offen";
const DEFAULT_REMINDER_BODY_DE = `Hallo {first_name},

deine Zeiteinträge für {month} im Projekt „{project}" fehlen noch. Bitte ergänze sie bald — es dauert nur wenige Minuten.

Danke!`;

const MAIL_DEFAULTS = {
  inviteSubject: DEFAULT_INVITE_SUBJECT_DE,
  inviteBody: DEFAULT_INVITE_BODY_DE,
  subject: DEFAULT_REMINDER_SUBJECT_DE,
  body: DEFAULT_REMINDER_BODY_DE,
};

export function ProjectCommunicationStep({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en" : "de";
  const defaults = MAIL_DEFAULTS;
  const panel = useProjectReminders(projectId);
  const save = useAdminSaveReminders(projectId);
  const send = useAdminSendReminders(projectId);
  const sendInvites = useAdminSendInvites(projectId);
  const copyInviteLink = useAdminCopyInviteLink(projectId);

  const settings = panel.data?.settings;

  const [weekday, setWeekday] = useState<number | null>(null);
  const [hour, setHour] = useState<number | null>(null);
  const [active, setActive] = useState<boolean | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inviteSelected, setInviteSelected] = useState<Set<string>>(new Set());
  const [confirmInvite, setConfirmInvite] = useState(false);

  const [inviteSubjectDraft, setInviteSubjectDraft] = useState<string | null>(null);
  const [inviteBodyDraft, setInviteBodyDraft] = useState<string | null>(null);
  const [reminderSubjectDraft, setReminderSubjectDraft] = useState<string | null>(null);
  const [reminderBodyDraft, setReminderBodyDraft] = useState<string | null>(null);

  const wd = weekday ?? settings?.weekday ?? 5;
  const hr = hour ?? settings?.hour ?? 9;
  const act = active ?? settings?.active ?? false;
  const inviteSubject = inviteSubjectDraft ?? settings?.inviteSubject ?? defaults.inviteSubject;
  const inviteBody = inviteBodyDraft ?? settings?.inviteBody ?? defaults.inviteBody;
  const reminderSubject = reminderSubjectDraft ?? settings?.subject ?? defaults.subject;
  const reminderBody = reminderBodyDraft ?? settings?.body ?? defaults.body;

  const assignees = useMemo(() => panel.data?.assignees ?? [], [panel.data?.assignees]);
  const missing = useMemo(() => assignees.filter((row) => row.missing), [assignees]);
  const year = panel.data?.year ?? Number(berlinToday().slice(0, 4));
  const month = panel.data?.month ?? Number(berlinToday().slice(5, 7));
  const monthLabel = useMemo(
    () =>
      new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(
        locale === "en" ? "en-US" : "de-DE",
        {
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        },
      ),
    [year, month, locale],
  );

  const reminderLog = useMemo(
    () => (panel.data?.log ?? []).filter((entry) => entry.kind === "reminder"),
    [panel.data?.log],
  );

  const lastSentByUser = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of reminderLog) {
      if (!map.has(entry.userId)) map.set(entry.userId, entry.createdAt);
    }
    return map;
  }, [reminderLog]);

  const lastReminderRun = reminderLog[0]?.createdAt ?? null;
  const monthAlreadySent = useMemo(() => {
    if (!lastReminderRun) return false;
    const sent = new Date(lastReminderRun);
    return sent.getFullYear() === year && sent.getMonth() + 1 === month;
  }, [lastReminderRun, year, month]);

  const nextRun = useMemo(() => nextWeekdayRun(wd, hr), [wd, hr]);

  function settingsPayload() {
    return {
      weekday: wd,
      hour: hr,
      active: act,
      subject: reminderSubject,
      body: reminderBody,
      inviteSubject,
      inviteBody,
    };
  }

  function persist(toastKey: "settingsSaved" | "templateSaved" = "settingsSaved") {
    save.mutate(settingsPayload(), {
      onSuccess: () => toast.success(t(`adminExtra.${toastKey}`)),
      onError: (error) => toast.error(describeError(error)),
    });
  }

  function remind(userIds?: string[]) {
    send.mutate(
      { locale: "de", userIds },
      {
        onSuccess: (data) => {
          toast.success(t("adminExtra.reminderSent", { count: data.sent }));
          setSelected(new Set());
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function invite(userIds: string[]) {
    sendInvites.mutate(
      { locale: "de", userIds },
      {
        onSuccess: (data) => {
          toast.success(t("adminExtra.inviteSent", { count: data.sent }));
          setInviteSelected(new Set());
          setConfirmInvite(false);
        },
        onError: (error) => toastApiError(error),
      },
    );
  }

  function copyLink(userId: string) {
    copyInviteLink.mutate(userId, {
      onSuccess: async (data) => {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success(t("adminExtra.inviteLinkCopied"));
        } catch {
          toast.error(t("adminExtra.inviteLinkCopyFailed"));
        }
      },
      onError: (error) => toastApiError(error),
    });
  }

  function toggle(userId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function toggleInvite(userId: string) {
    setInviteSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  if (panel.isLoading) return <Skeleton className="mt-10 h-40 w-full rounded-xl" />;
  if (!panel.data) return null;

  const inviteBulkLabel =
    inviteSelected.size > 0
      ? t("adminExtra.inviteSendBulk", { count: inviteSelected.size })
      : t("adminExtra.selectionEmpty");

  return (
    <div className="mt-10 space-y-10" data-testid="live-communication-step">
      <div>
        <h2 className="text-2xl">{t("adminExtra.communicationTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("adminExtra.communicationIntro")}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 size-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground" data-testid="communication-placeholders">
            {t("adminExtra.placeholderHelp")}{" "}
            <code className="rounded bg-muted px-1">{"{first_name}"}</code>,{" "}
            <code className="rounded bg-muted px-1">{"{name}"}</code>,{" "}
            <code className="rounded bg-muted px-1">{"{project}"}</code>,{" "}
            <code className="rounded bg-muted px-1">{"{month}"}</code>
            {". "}
            {t("adminExtra.placeholderHelpDetail")}
          </p>
        </div>
      </div>

      <section className="grid gap-6 lg:grid-cols-[1fr_1.1fr]" data-testid="invite-section">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="text-2xl">{t("adminExtra.inviteTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("adminExtra.inviteIntro")}</p>
          <div className="mt-4 grid gap-3">
            <div className="space-y-1.5">
              <Label>{t("adminExtra.inviteSubject")}</Label>
              <Input
                value={inviteSubject}
                onChange={(e) => setInviteSubjectDraft(e.target.value)}
                data-testid="invite-subject"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("adminExtra.inviteBody")}</Label>
              <Textarea
                rows={12}
                value={inviteBody}
                onChange={(e) => setInviteBodyDraft(e.target.value)}
                data-testid="invite-body"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => persist("templateSaved")}
              disabled={save.isPending}
              data-testid="invite-save-template"
            >
              <Save className="size-4" />
              {t("adminExtra.saveTemplate")}
            </Button>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-lg">{t("adminExtra.recipientsPlain")}</h3>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setInviteSelected(new Set(assignees.map((row) => row.userId)))}
              >
                {t("adminExtra.selectAll")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setInviteSelected(new Set())}>
                {t("adminExtra.selectNone")}
              </Button>
            </div>
          </div>
          <div className="mt-2 overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-sm" data-testid="invite-recipients">
              <tbody>
                {assignees.map((person) => (
                  <tr key={person.userId} className="border-b border-border last:border-b-0">
                    <td className="w-8 px-3 py-2">
                      <Checkbox
                        checked={inviteSelected.has(person.userId)}
                        onCheckedChange={() => toggleInvite(person.userId)}
                        disabled={!person.email}
                        aria-label={t("adminExtra.selectPerson", {
                          name: formatPersonName(person),
                        })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{formatPersonName(person)}</div>
                      <div className="text-xs text-muted-foreground">
                        {person.email || (
                          <span className="text-destructive">{t("adminExtra.noEmail")}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!person.email || copyInviteLink.isPending}
                          onClick={() => copyLink(person.userId)}
                          data-testid={`invite-copy-link-${person.userId}`}
                        >
                          <Copy className="size-3.5" /> {t("adminExtra.copyInvitationLink")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!person.email || sendInvites.isPending}
                          onClick={() => invite([person.userId])}
                          data-testid={`invite-send-one-${person.userId}`}
                        >
                          <Send className="size-3.5" /> {t("adminExtra.sendIndividually")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {assignees.length === 0 && (
                  <tr>
                    <td className="p-6 text-center text-sm text-muted-foreground">
                      {t("projectLive.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {t("adminExtra.selectedCount", { count: inviteSelected.size })}
            </div>
            <Button
              disabled={inviteSelected.size === 0 || sendInvites.isPending}
              onClick={() => setConfirmInvite(true)}
              data-testid="invite-send-bulk"
            >
              <Mail className="size-4" />
              {inviteBulkLabel}
            </Button>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="text-2xl">{t("adminExtra.autoReminderTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("adminExtra.autoReminderIntro", { month: monthLabel })}
          </p>

          <div className="mt-4 grid gap-3">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <div className="text-sm font-medium">{t("adminExtra.reminderActive")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("adminExtra.reminderActiveHint", {
                    weekday: t(`adminExtra.weekday${wd}`),
                    hour: String(hr).padStart(2, "0"),
                  })}
                </div>
              </div>
              <Checkbox
                checked={act}
                onCheckedChange={(value) => setActive(value === true)}
                aria-label={t("adminExtra.reminderActive")}
                data-testid="reminder-active"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t("adminExtra.reminderWeekday")}</Label>
                <Select value={String(wd)} onValueChange={(value) => setWeekday(Number(value))}>
                  <SelectTrigger data-testid="reminder-weekday">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {t(`adminExtra.weekday${day}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("adminExtra.reminderHour")}</Label>
                <Select value={String(hr)} onValueChange={(value) => setHour(Number(value))}>
                  <SelectTrigger data-testid="reminder-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, "0")}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t("adminExtra.reminderSubject")}</Label>
              <Input
                value={reminderSubject}
                onChange={(e) => setReminderSubjectDraft(e.target.value)}
                data-testid="reminder-subject"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("adminExtra.reminderBody")}</Label>
              <Textarea
                rows={10}
                value={reminderBody}
                onChange={(e) => setReminderBodyDraft(e.target.value)}
                data-testid="reminder-body"
              />
            </div>

            <Button
              variant="outline"
              disabled={save.isPending}
              onClick={() => persist("settingsSaved")}
              data-testid="reminder-save"
            >
              {t("adminExtra.saveSettings")}
            </Button>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg">
              {t("adminExtra.recipientsTitle", {
                count: missing.length,
                missing: missing.length,
              })}
            </h3>
            {missing.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(new Set(missing.map((row) => row.userId)))}
                >
                  {t("adminExtra.selectAll")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  {t("adminExtra.selectNone")}
                </Button>
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("adminExtra.recipientsIntro", { month: monthLabel, missing: missing.length })}
          </p>

          <div
            className="mt-3 rounded-2xl border border-border bg-card p-4"
            data-testid="reminder-next-run"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                {!act ? (
                  <span className="text-muted-foreground">{t("adminExtra.nextRunDisabled")}</span>
                ) : nextRun.days === 0 ? (
                  <span className="font-medium">{t("adminExtra.nextRunToday")}</span>
                ) : (
                  <span>
                    {t("adminExtra.nextRunIn", { days: nextRun.days })}{" "}
                    <span className="text-muted-foreground">
                      ({nextRun.date.toLocaleDateString(locale === "en" ? "en-US" : "de-DE")})
                    </span>
                  </span>
                )}
              </div>
              <div className="text-xs">
                {monthAlreadySent && lastReminderRun ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-emerald-700">
                    <Check className="size-3" />
                    {t("adminExtra.alreadySent", {
                      when: new Date(lastReminderRun).toLocaleDateString(
                        locale === "en" ? "en-US" : "de-DE",
                      ),
                    })}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-muted-foreground">
                    {t("adminExtra.notYetSent", { month: monthLabel })}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-sm" data-testid="reminder-recipients">
              <tbody>
                {missing.length === 0 && (
                  <tr>
                    <td
                      className="p-6 text-center text-sm text-muted-foreground"
                      data-testid="reminder-recipients-empty"
                    >
                      {assignees.length === 0
                        ? t("projectLive.empty")
                        : t("adminExtra.recipientsNone", { month: monthLabel })}
                    </td>
                  </tr>
                )}
                {missing.map((person) => (
                  <RecipientRow
                    key={person.userId}
                    person={person}
                    checked={selected.has(person.userId)}
                    lastSent={lastSentByUser.get(person.userId) ?? null}
                    locale={locale}
                    busy={send.isPending}
                    onToggle={() => toggle(person.userId)}
                    onSend={() => remind([person.userId])}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {t("adminExtra.selectedCount", { count: selected.size })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={selected.size === 0 || send.isPending}
                onClick={() => remind([...selected])}
                data-testid="reminder-send-selection"
              >
                <Send className="size-4" />
                {t("adminExtra.sendSelection", { count: selected.size })}
              </Button>
              <Button
                disabled={missing.length === 0 || send.isPending}
                onClick={() => remind()}
                data-testid="reminder-send-missing"
              >
                <Mail className="size-4" />
                {t("adminExtra.sendMissing", { count: missing.length })}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-lg">{t("adminExtra.reminderLog")}</h3>
        {(panel.data.log.length === 0 && (
          <p className="mt-2 text-sm text-muted-foreground">{t("adminExtra.reminderLogEmpty")}</p>
        )) || (
          <div className="mt-3 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full text-sm" data-testid="reminder-send-log">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">{t("admin.emailCreated")}</th>
                  <th className="px-3 py-2">{t("adminExtra.reminderLogKind")}</th>
                  <th className="px-3 py-2">{t("employees.colName")}</th>
                  <th className="px-3 py-2">{t("admin.emailStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {panel.data.log.map((entry) => (
                  <tr key={entry.id} className="border-b border-border/60">
                    <td className="px-3 py-2">
                      {new Date(entry.createdAt).toLocaleString(
                        locale === "en" ? "en-US" : "de-DE",
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          entry.kind === "invite"
                            ? "inline-flex rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-700"
                            : "inline-flex rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700"
                        }
                      >
                        {entry.kind === "invite"
                          ? t("adminExtra.logKindInvite")
                          : t("adminExtra.logKindReminder")}
                      </span>
                    </td>
                    <td className="px-3 py-2">{formatPersonName(entry)}</td>
                    <td className="px-3 py-2">{entry.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={confirmInvite} onOpenChange={(open) => !open && setConfirmInvite(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("adminExtra.inviteConfirmTitle", { count: inviteSelected.size })}
            </DialogTitle>
            <DialogDescription>{t("adminExtra.inviteConfirmBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmInvite(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => invite([...inviteSelected])}
              disabled={sendInvites.isPending}
              data-testid="invite-confirm-send"
            >
              <Send className="size-4" />
              {t("adminExtra.inviteSendBulk", { count: inviteSelected.size })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecipientRow({
  person,
  checked,
  lastSent,
  locale,
  busy,
  onToggle,
  onSend,
}: {
  person: ReminderAssignee;
  checked: boolean;
  lastSent: string | null;
  locale: "de" | "en";
  busy: boolean;
  onToggle: () => void;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="w-8 px-3 py-2">
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          disabled={!person.email}
          aria-label={t("adminExtra.selectPerson", {
            name: formatPersonName(person),
          })}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-2 font-medium">
          {formatPersonName(person)}
          {person.missing ? (
            <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              {t("adminExtra.badgeMissing")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
              <Check className="size-3" /> {t("adminExtra.badgeEntered")}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {person.email || <span className="text-destructive">{t("adminExtra.noEmail")}</span>}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        {lastSent ? (
          <span className="text-xs text-muted-foreground">
            {t("adminExtra.lastSent", {
              when: new Date(lastSent).toLocaleDateString(locale === "en" ? "en-US" : "de-DE"),
            })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground/60">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          variant="ghost"
          size="sm"
          disabled={!person.email || busy}
          onClick={onSend}
          data-testid={`reminder-send-one-${person.userId}`}
        >
          <Send className="size-3.5" /> {t("adminExtra.sendIndividually")}
        </Button>
      </td>
    </tr>
  );
}

/** Next occurrence of ISO weekday (1=Mon…7=Sun) at `hour`:00, measured from Europe/Berlin today. */
function nextWeekdayRun(weekday: number, hour: number): { days: number; date: Date } {
  const today = berlinToday();
  const todayIso = isoWeekday(today);
  let delta = weekday - todayIso;
  if (delta < 0) delta += 7;
  if (delta === 0) {
    const nowHour = berlinHour();
    if (nowHour >= hour) delta = 7;
  }
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d + delta, hour, 0, 0, 0);
  return { days: delta, date };
}
